import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { putDataPromise } from 'brock-fetch-utils';
import {
    cancelEnrollmentUri,
    getEnrollmentUri,
} from '../constants/apiConstants';
import { Trans, useTranslation } from 'react-i18next';
import {
    AccentLabel,
    AnimatedLoader,
    AnimatedPageLoader,
    HorizontalFlex,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    PadContainer,
    Ticket,
    TicketBody,
    TicketHeader,
    VerticalFlex,
} from 'smartsuite-ui';
import { orderBy } from 'natural-orderby';
import { countriesEnglish } from '../constants/countries';
import { DateTimeFormatter } from 'brock-date-time';
import { useState } from 'react';
import { formatPhoneNumber } from '../utils/formatters';

function CancelEnrollmentPage() {
    const { t } = useTranslation();
    const { reservationId, reservationLastName } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const enrollmentQuery = useQuery({
        queryKey: ['enrollment', reservationId, reservationLastName],
        queryFn: async () => {
            const response = await fetch(
                getEnrollmentUri(reservationId, reservationLastName)
            );

            if (!response.ok) {
                navigate('/');
            }
            const parsedResponse = await response.json();

            const orderedPassengers = orderBy(
                parsedResponse.passengers,
                ['isPrimary'],
                ['desc']
            );

            return { ...parsedResponse, passengers: orderedPassengers };
        },
    });
    const { data: enrollment } = enrollmentQuery;

    const cancelEnrollmentMutation = useMutation({
        mutationFn: (payload) =>
            putDataPromise(
                cancelEnrollmentUri(payload.reservationId, payload.lastName)
            ),
        onSuccess: () => {
            queryClient.invalidateQueries([
                'enrollment',
                reservationId,
                reservationLastName,
            ]);

            setIsSuccessModalOpen(true);
        },
    });

    const handleClickCancel = () => {
        cancelEnrollmentMutation.mutate({
            reservationId: reservationId,
            lastName: reservationLastName,
        });
    };

    const handleClickBack = () => {
        navigate('..');
    };

    if (!enrollment) {
        return <AnimatedPageLoader />;
    }

    if (enrollment.cancelledTimestamp) {
        return (
            <div className="page-wrapper">
                <div className="cancel-enrollment">
                    <header className="modify-contact-details__header">
                        <div className="cancel-enrollment__title">
                            {t('enrollmentCanceledTitle')}
                        </div>
                    </header>
                    <p>
                        <Trans
                            i18nKey={'reactivateEnrollmentDescription'}
                            components={[
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <a href="mailto:bcsupport@bagcheck.com" />,
                            ]}
                        />
                    </p>
                    <CancelDetailsCard enrollment={enrollment} />
                    {isSuccessModalOpen && (
                        <Modal
                            onOverlayClick={() => setIsSuccessModalOpen(false)}
                        >
                            <ModalHeader
                                onDismiss={() => setIsSuccessModalOpen(false)}
                            >
                                Enrollment Canceled
                            </ModalHeader>
                            <ModalBody>
                                <p>Cancellation Successful</p>
                            </ModalBody>
                        </Modal>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="cancel-enrollment">
                <header className="modify-contact-details__header">
                    <div className="cancel-enrollment__title">
                        {t('cancelEnrollmentTitle')}
                    </div>
                </header>
                <p>{t('cancelEnrollmentDescription')}</p>
                <PadContainer cssClassModifiers={['pad-small']} />
                <div className="cancel-enrollment__cancel-btns-container">
                    <button
                        className="btn btn--cancel"
                        onClick={handleClickCancel}
                        disabled={cancelEnrollmentMutation.isLoading}
                    >
                        {cancelEnrollmentMutation.isLoading ? (
                            <HorizontalFlex
                                cssClassModifiers={['justify-center']}
                            >
                                <AnimatedLoader />
                            </HorizontalFlex>
                        ) : (
                            <span>Cancel Enrollment</span>
                        )}
                    </button>
                    <button
                        className="btn btn--secondary"
                        onClick={handleClickBack}
                    >
                        {t('back')}
                    </button>
                </div>
                <PadContainer cssClassModifiers={['pad-medium']} />
                <CancelDetailsCard enrollment={enrollment} />
            </div>
        </div>
    );
}

function CancelDetailsCard(props) {
    const { t } = useTranslation();
    const primaryPassenger = props.enrollment?.passengers.find(
        (passenger) => passenger.isPrimary
    );

    return (
        <div
            className={`cancel-enrollment__cancel-details-card ${
                props.enrollment.cancelledTimestamp
                    ? 'cancel-enrollment__cancel-details-card__muted'
                    : ''
            }`}
        >
            <div className="cancel-enrollment__subtitle">
                <span>{t('enrollmentDetailsSubtitle')}</span>
            </div>
            <hr className="cancel-enrollment__divider" />
            <div className="cancel-enrollment__container">
                <PadContainer cssClassModifiers={['pad-small']}>
                    <VerticalFlex>
                        <HorizontalFlex cssClassModifiers={['space-between']}>
                            <span>{t('reservationNumber')}</span>
                            <span className="cancel-enrollment__enrollment-detail">
                                {props.enrollment.reservationId}
                            </span>
                        </HorizontalFlex>
                        <HorizontalFlex cssClassModifiers={['space-between']}>
                            <span>{t('arrivalDate')}</span>
                            <span className="cancel-enrollment__enrollment-detail">
                                {DateTimeFormatter.withFormatString(
                                    primaryPassenger.checkinDate,
                                    'MM/DD/YYYY'
                                )}
                            </span>
                        </HorizontalFlex>
                        <HorizontalFlex cssClassModifiers={['space-between']}>
                            <span>{t('disneyResortHotel')}</span>
                            <span className="cancel-enrollment__enrollment-detail">
                                {props.enrollment.resortName}
                            </span>
                        </HorizontalFlex>
                        <HorizontalFlex cssClassModifiers={['space-between']}>
                            <span>{t('guestEmail')}</span>
                            <span className="confirmation-card__enrollment-detail">
                                {props.enrollment.primaryEmail}
                            </span>
                        </HorizontalFlex>
                        <HorizontalFlex cssClassModifiers={['space-between']}>
                            <span>{t('guestPhoneNumber')}</span>
                            <span className="confirmation-card__enrollment-detail">
                                {formatPhoneNumber(
                                    props.enrollment.primaryPhone
                                )}
                            </span>
                        </HorizontalFlex>
                    </VerticalFlex>
                </PadContainer>
            </div>
            <PadContainer cssClassModifiers={['pad-small']} />
            <div className="cancel-enrollment__subtitle">
                {t('guestDetailsSubtitle')}
            </div>
            <hr className="cancel-enrollment__divider" />
            <div className="cancel-enrollment__container">
                <PadContainer cssClassModifiers={['pad-small']}>
                    <VerticalFlex>
                        {props.enrollment.passengers
                            .filter((pax) => pax.isEligible)
                            .map((passenger, index) => (
                                <>
                                    {index > 0 && (
                                        <PadContainer
                                            cssClassModifiers={['pad-small']}
                                        />
                                    )}
                                    <Ticket>
                                        <TicketHeader>
                                            <VerticalFlex>
                                                <AccentLabel>
                                                    Guest Name
                                                </AccentLabel>
                                                <Label>{`${passenger.lastName}, ${passenger.firstName}`}</Label>
                                            </VerticalFlex>

                                            {passenger.isPrimary && (
                                                <div>
                                                    <PadContainer
                                                        cssClassModifiers={[
                                                            'pad-small',
                                                        ]}
                                                    />
                                                    <span
                                                        className={`cancel-enrollment__badge ${
                                                            props.enrollment
                                                                .cancelledTimestamp
                                                                ? 'cancel-enrollment__badge--muted'
                                                                : 'cancel-enrollment__badge--primary-guest'
                                                        }`}
                                                    >
                                                        Primary Guest
                                                    </span>
                                                </div>
                                            )}
                                        </TicketHeader>
                                        <TicketBody>
                                            <HorizontalFlex
                                                cssClassModifiers={[
                                                    'align-flex-start',
                                                ]}
                                            >
                                                <VerticalFlex
                                                    cssClassModifiers={[
                                                        'small-relative',
                                                    ]}
                                                >
                                                    <AccentLabel>
                                                        Flight Number
                                                    </AccentLabel>
                                                    <Label>{`${passenger.airlineCode}${passenger.flightNumber}`}</Label>
                                                </VerticalFlex>
                                                <PadContainer
                                                    cssClassModifiers={[
                                                        'pad-small',
                                                    ]}
                                                />
                                                <VerticalFlex
                                                    cssClassModifiers={[
                                                        'small-relative',
                                                    ]}
                                                >
                                                    <AccentLabel>
                                                        Confirmation Number
                                                    </AccentLabel>
                                                    <Label>
                                                        {passenger.pnr}
                                                    </Label>
                                                </VerticalFlex>
                                            </HorizontalFlex>
                                        </TicketBody>
                                    </Ticket>
                                </>
                            ))}
                    </VerticalFlex>
                </PadContainer>
            </div>
            <PadContainer cssClassModifiers={['pad-small']} />
            <div className="cancel-enrollment__subtitle">
                {t('luggageTagDetailsSubtitle')}
            </div>
            <hr className="cancel-enrollment__divider" />
            <div className="cancel-enrollment__container">
                <PadContainer cssClassModifiers={['pad-small']}>
                    <VerticalFlex>
                        {props.enrollment.addresses.map((address, index) => (
                            <>
                                {index > 0 && (
                                    <PadContainer
                                        cssClassModifiers={['pad-small']}
                                    />
                                )}
                                <Ticket>
                                    <TicketHeader>
                                        <VerticalFlex>
                                            <AccentLabel>
                                                Number of Tags
                                            </AccentLabel>
                                            <Label>
                                                {address.numberOfTags}
                                            </Label>
                                        </VerticalFlex>
                                    </TicketHeader>
                                    <TicketBody>
                                        <HorizontalFlex
                                            cssClassModifiers={[
                                                'align-flex-start',
                                            ]}
                                        >
                                            <VerticalFlex
                                                cssClassModifiers={[
                                                    'small-relative',
                                                ]}
                                            >
                                                <AccentLabel>
                                                    Address
                                                </AccentLabel>
                                                <Label>{`${address.addressLine1},`}</Label>
                                                <Label>
                                                    {`${address.city}, ${
                                                        address.state
                                                    }, ${
                                                        countriesEnglish.find(
                                                            (country) =>
                                                                country.valueField ===
                                                                address.country
                                                        )?.valueField
                                                    }`}
                                                </Label>
                                            </VerticalFlex>
                                            <VerticalFlex
                                                cssClassModifiers={[
                                                    'small-relative',
                                                ]}
                                            >
                                                <AccentLabel>
                                                    Recipient Name
                                                </AccentLabel>
                                                <Label>
                                                    {address.recipientName}
                                                </Label>
                                            </VerticalFlex>
                                        </HorizontalFlex>
                                    </TicketBody>
                                </Ticket>
                            </>
                        ))}
                    </VerticalFlex>
                </PadContainer>
            </div>
        </div>
    );
}

export default CancelEnrollmentPage;
